import { useLingui } from '@lingui/react/macro';
import type { QueryKey } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { FormProvider, type FieldValues } from 'react-hook-form';

import { useIsMobile } from '@/shared/mobile';
import { BlockSkeleton, ErrorState } from '@/shared/page';
import { ActionRunnerProvider, PageHeader, type ActionSpec } from '@/shared/patterns';
import { Alert } from '@/shared/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';

import type { AgentFill } from './agent-fill';
import { AgentFillNotice, AgentFillProvider } from './agent-fill-ui';
import { FormStateProvider } from './fields';
import type { FormScreen as FormScreenModel } from './use-form-screen';

export interface FormScreenProps<V extends FieldValues, R> {
  readonly screen: FormScreenModel<V, R>;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly back?: { readonly label: string; readonly onClick: () => void };
  /** Cancelar: em geral volta para a tela-mãe (o guarda pergunta se há alteração). */
  readonly onCancel?: () => void;
  /** Ações do registro no cabeçalho (manifesto): excluir, clonar… Nunca o Salvar, que é do formulário. */
  readonly actions?: readonly ActionSpec[];
  readonly submitLabel?: string;
  /** Chaves invalidadas depois de uma ação do cabeçalho com efeito. */
  readonly invalidate?: readonly QueryKey[];
  /**
   * Cabeçalho próprio no lugar do `PageHeader` (o `DetailHeader` do registro com abas, P2). A tela
   * põe o Salvar na barra dela (web); no iPhone o Salvar preso embaixo continua sendo o do formulário.
   */
  readonly header?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  /** O motor do preenchimento pelo Luy (`useAgentFill`, U56): selo nos campos e o aviso com "Desfazer". */
  readonly agentFill?: AgentFill;
}

/**
 * Arquétipo Formulário (U39 §1; formulário não é bloco, U41): cabeçalho, avisos (somente leitura,
 * erro de validação, recusa da API), seções e a barra de ação, presa embaixo no iPhone (U12). Com
 * alteração não salva, sair da tela pede confirmação.
 */
export function FormScreen<V extends FieldValues, R>(props: FormScreenProps<V, R>) {
  return (
    <ActionRunnerProvider {...(props.invalidate ? { invalidate: props.invalidate } : {})}>
      <FormScreenContent {...props} />
    </ActionRunnerProvider>
  );
}

function FormScreenContent<V extends FieldValues, R>({ screen, title, description, back, onCancel, actions, submitLabel, header: customHeader, children, className, agentFill }: FormScreenProps<V, R>) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const formRef = useRef<HTMLFormElement>(null);
  const { form, readOnly, isLoading, loadError, apiError, unmatchedErrors, isSubmitting, errorEpoch } = screen;
  const invalidCount = Object.keys(form.formState.errors).length;

  // Foca o primeiro campo inválido de cada tentativa com erro. Sem dependências: o erro do RHF pode
  // chegar num render depois do `errorEpoch`, e só então o `aria-invalid` existe no DOM.
  const focused = useRef(0);
  useEffect(() => {
    if (errorEpoch === 0 || focused.current === errorEpoch) return;
    const first = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!first) return;
    first.focus();
    focused.current = errorEpoch;
  });

  const save = submitLabel ?? t`Salvar`;
  const header = customHeader ?? (
    <PageHeader
      title={title}
      description={description}
      {...(back ? { back } : {})}
      {...(actions ? { actions } : {})}
      aside={
        !isMobile && !readOnly ? (
          <div className="flex items-center gap-2">
            {onCancel ? (
              <Button variant="ghost" onClick={onCancel}>
                {t`Cancelar`}
              </Button>
            ) : null}
            <Button loading={isSubmitting} disabled={isLoading || loadError !== undefined} onClick={() => void screen.submit()}>
              <Save aria-hidden />
              {save}
            </Button>
          </div>
        ) : undefined
      }
    />
  );

  if (loadError) {
    return (
      <div className={cn('flex flex-col gap-5', className)}>
        {header}
        <div className="rounded-card border border-border bg-card">
          <ErrorState error={loadError} onRetry={screen.reload} compact={isMobile} />
        </div>
      </div>
    );
  }

  return (
    <div data-slot="form-screen" className={cn('flex min-w-0 flex-col gap-5', className)}>
      {header}
      {readOnly ? (
        <Alert tone="info" title={t`Somente leitura`}>
          {t`Você não tem permissão para alterar este cadastro. Peça a quem administra os acessos do seu correspondente.`}
        </Alert>
      ) : null}
      {apiError && !apiError.isCanceled ? (
        <Alert tone="err" title={t`Não foi possível salvar`}>
          <p>{apiError.feedback().description}</p>
          {unmatchedErrors.length > 0 ? (
            <ul className="mt-1 list-disc pl-5">
              {unmatchedErrors.map((error) => (
                <li key={`${error.key}:${error.helpMessage}`}>{error.helpMessage}</li>
              ))}
            </ul>
          ) : null}
        </Alert>
      ) : invalidCount > 0 && form.formState.isSubmitted ? (
        <Alert tone="err" title={t`Não foi possível salvar`}>
          {invalidCount === 1 ? t`Corrija o campo marcado e tente de novo.` : t`Corrija os ${invalidCount} campos marcados e tente de novo.`}
        </Alert>
      ) : null}
      <AgentFillNotice fill={agentFill} />
      {isLoading ? (
        <div className="rounded-card border border-border bg-card p-6">
          <span className="sr-only">{t`Carregando formulário`}</span>
          <BlockSkeleton kind="record" rows={6} />
        </div>
      ) : (
        <FormProvider {...form}>
          <AgentFillProvider fill={agentFill}>
            <FormStateProvider readOnly={readOnly}>
              <form ref={formRef} noValidate onSubmit={(event) => void screen.submit(event)} className="flex flex-col gap-5">
                {children}
                {isMobile && !readOnly ? (
                  <div className="sticky bottom-0 z-20 -mx-4 border-t border-border bg-background/95 px-4 pt-3 pb-3 backdrop-blur">
                    <Button type="submit" size="lg" block loading={isSubmitting}>
                      <Save aria-hidden />
                      {save}
                    </Button>
                  </div>
                ) : null}
              </form>
            </FormStateProvider>
          </AgentFillProvider>
        </FormProvider>
      )}
      <AlertDialog open={screen.guard.blocked} onOpenChange={(open) => (open ? undefined : screen.guard.stay())}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Descartar as alterações?`}</AlertDialogTitle>
            <AlertDialogDescription>{t`O que você mudou neste formulário ainda não foi salvo e vai se perder.`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={screen.guard.stay}>{t`Continuar editando`}</AlertDialogCancel>
            <AlertDialogAction tone="danger" onClick={screen.guard.proceed}>
              {t`Sim, descartar`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
